import { AttachmentType } from 'lib/enum/attachment-type.enum';
import { ConversationMessageMediaItemType } from 'lib/enum/conversation-message-media-item-type.enum';
import { ConversationMessageType } from 'lib/enum/conversation-message-type.enum';
import { InboxMessageOrigin } from 'lib/enum/inbox-message-origin.enum';
import { TalentConversationLocation } from 'lib/enum/lead-conversation-location';
import { ChannelType } from 'lib/enum/message-channel.enum';
import { MessageDirection } from 'lib/enum/message-direction.enum';
import { MessageStatus } from 'lib/enum/message-status.enum';
import {
    ConversationMessageContent,
    ReceivedEmailMessageContent,
    InteractiveConversationMessageContent,
    InteractiveConversationMessageReplyContent,
    WaTemplatesConversationMessageContent,
    SentEmailMessageContent,
    NoteMessageContent,
    WebChatMessageContent,
} from 'lib/types/conversation-message-content-type';
import { Iri } from 'lib/utils/iri';
import { ActiveAgentSession } from '../LeadsConversation';
import { TalentEntry } from '../TalentEntries';
import { TalentApplication } from '../TalentApplications';

export enum SessionEventName {
    MessageSent = 'message_sent',
    SessionCompleted = 'session_completed',
    TalentUpdated = 'talent_updated',
    ConsentUpdated = 'consent_updated',
    ConversationLocationChanged = 'conversation_location_changed',
    SummaryGenerated = 'summary_generated',
    SessionPutOnStandby = 'session_put_on_standby',
    SessionResumed = 'session_resumed',
    SessionActivated = 'session_activated',
    ApplicationCreatedInKomboAts = 'application_created_in_kombo_ats',
    ActivationContextProvided = 'activation_context_provided',
}

export enum SessionOutcome {
    Resolved = 'resolved',
    Timeout = 'timeout',
    ManualTakeover = 'manual_takeover',
}

export enum ConversationEventLocation {
    Archive = 'archive',
    Spam = 'spam',
}

export type MessageSentData = {
    messageId: string;
};

export type SessionCompletedData = {
    outcome: SessionOutcome;
    userId?: string;
};

export type TalentUpdatedData = {
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
    phone?: string | null;
    whatsappUsername?: string | null;
    location?: string | null;
    jobTitle?: string | null;
    linkedinProfilePage?: string | null;
    currentEmployer?: string | null;
    rating?: number | null;
    assigneeId?: string | null;
    statusId?: string | null;
    talentpoolId?: string | null;
    tagsIdsAdded?: string[];
    customFields?: Record<string, any>;
};

export enum ConsentUpdateType {
    OptIn = 'opt_in',
    OptOut = 'opt_out',
}

export type ConsentUpdatedData = {
    consentType: ConsentUpdateType;
    expiresAt: string | null;
};

export type SummaryGeneratedData = {
    summary: string;
};

export type ConversationLocationChangedData = {
    location: ConversationEventLocation;
};

export type ApplicationCreatedInKomboAtsData = {
    komboIntegrationId: string;
    komboJobId: string;
    atsJobId: string;
    jobName: string | null;
};

export type SessionActivatedData = {
    activationContext: string | null;
};

export type ActivationContextProvidedData = {
    context: string;
};

export type AgentSessionEventData =
    | MessageSentData
    | SessionCompletedData
    | TalentUpdatedData
    | ConsentUpdatedData
    | SummaryGeneratedData
    | ConversationLocationChangedData
    | SessionActivatedData
    | ActivationContextProvidedData
    | ApplicationCreatedInKomboAtsData;

export type AgentSessionEvent = {
    id?: string;
    sessionId?: string;
    requestId?: string | null;
    createdAt: string;
    name: SessionEventName;
    data: AgentSessionEventData;
};

export const TALENT_PROPERTY_FIELDS: ReadonlyArray<keyof TalentUpdatedData> = [
    'firstName',
    'lastName',
    'email',
    'phone',
    'whatsappUsername',
    'location',
    'jobTitle',
    'linkedinProfilePage',
    'currentEmployer',
    'customFields',
] as const;

export const TALENT_MANAGEMENT_FIELDS: ReadonlyArray<keyof TalentUpdatedData> = [
    'rating',
    'assigneeId',
    'statusId',
    'talentpoolId',
    'tagsIdsAdded',
] as const;

export type Conversation = {
    messages: ConversationsMessage[];
    unread: boolean;
    location: TalentConversationLocation;
    activeAgentSession: ActiveAgentSession | null;
    agentSessionEvents: AgentSessionEvent[];
};

export type NoteConversationMessage = {
    id: string;
    conversation: Iri;
    channel: 'note';
    createdAt: string;
    direction: MessageDirection.Sent;
    sender: Iri;
    status: MessageStatus.Unknown;
    message: NoteMessageContent;
    origin: InboxMessageOrigin.MrworkInbox;
    messageType: ConversationMessageType.Note;
};

export type SentConversationMessage =
    | {
          id: string;
          conversation: Iri;
          channel: Iri;
          createdAt: string;
          direction: MessageDirection.Sent;
          sender: Iri;
          status: MessageStatus;
          message: ConversationMessageContent;
          origin: InboxMessageOrigin.MrworkInbox;
          messageType: ConversationMessageType.WhatsappBasicSent;
          agentExplanation: string | null;
      }
    | {
          id: string;
          conversation: Iri;
          channel: Iri;
          createdAt: string;
          direction: MessageDirection.Sent;
          sender: null;
          status: MessageStatus;
          message: ConversationMessageContent;
          origin: Exclude<InboxMessageOrigin, InboxMessageOrigin.MrworkInbox>;
          messageType: ConversationMessageType.WhatsappBasicSent;
          agentExplanation: string | null;
      }
    | {
          id: string;
          conversation: Iri;
          channel: Iri;
          createdAt: string;
          direction: MessageDirection.Sent;
          sender: Iri;
          status: MessageStatus;
          message: SentEmailMessageContent;
          origin: InboxMessageOrigin.MrworkInbox;
          messageType: ConversationMessageType.EmailSent;
          agentExplanation: string | null;
      }
    | {
          id: string;
          conversation: Iri;
          channel: Iri;
          createdAt: string;
          direction: MessageDirection.Sent;
          sender: null;
          status: MessageStatus;
          message: SentEmailMessageContent;
          origin: InboxMessageOrigin.ConversationalAgent;
          messageType: ConversationMessageType.EmailSent;
          agentExplanation: string | null;
      }
    | {
          id: string;
          conversation: Iri;
          channel: Iri;
          createdAt: string;
          direction: MessageDirection.Sent;
          sender: null;
          status: MessageStatus;
          message: SentEmailMessageContent;
          origin: Exclude<InboxMessageOrigin, InboxMessageOrigin.MrworkInbox | InboxMessageOrigin.ConversationalAgent>;
          messageType: ConversationMessageType.EmailSent;
          agentExplanation: string | null;
      }
    | {
          id: string;
          conversation: Iri;
          channel: Iri;
          createdAt: string;
          direction: MessageDirection.Sent;
          sender: null;
          status: MessageStatus;
          message: WaTemplatesConversationMessageContent;
          origin: Exclude<InboxMessageOrigin, InboxMessageOrigin.MrworkInbox>;
          messageType: ConversationMessageType.WhatsappTemplateSent;
          templateId: string;
          agentExplanation: string | null;
      }
    | {
          id: string;
          conversation: Iri;
          channel: Iri;
          createdAt: string;
          direction: MessageDirection.Sent;
          sender: Iri;
          status: MessageStatus;
          messageType: ConversationMessageType.UnsupportedSent;
          origin: Exclude<InboxMessageOrigin, InboxMessageOrigin.MrworkInbox>;
          agentExplanation: string | null;
      }
    | {
          id: string;
          conversation: Iri;
          channel: Iri;
          createdAt: string;
          direction: MessageDirection.Sent;
          sender: null;
          status: MessageStatus;
          message: InteractiveConversationMessageContent;
          origin: Exclude<InboxMessageOrigin, InboxMessageOrigin.MrworkInbox>;
          messageType: ConversationMessageType.WhatsappInteractiveSent;
          templateId: string;
          agentExplanation: string | null;
      }
    | {
          id: string;
          conversation: Iri;
          channel: Iri;
          createdAt: string;
          direction: MessageDirection.Sent;
          sender: Iri;
          status: MessageStatus;
          message: ConversationMessageContent;
          origin: InboxMessageOrigin.MrworkInbox;
          messageType: ConversationMessageType.SmsSent;
          agentExplanation: string | null;
      }
    | {
          id: string;
          conversation: Iri;
          channel: Iri;
          createdAt: string;
          direction: MessageDirection.Sent;
          sender: Iri;
          status: MessageStatus;
          message: ConversationMessageContent;
          origin: InboxMessageOrigin.MrworkInbox;
          messageType: ConversationMessageType.AiSent;
          agentExplanation: string | null;
      }
    | {
          id: string;
          conversation: Iri;
          channel: Iri;
          createdAt: string;
          direction: MessageDirection.Sent;
          sender: Iri | null;
          status: MessageStatus;
          message: ConversationMessageContent;
          origin: InboxMessageOrigin;
          messageType: ConversationMessageType.WebChatSent;
          agentExplanation: string | null;
      }
    | NoteConversationMessage;

export type ReceivedConversationMessage =
    | {
          id: string;
          conversation: Iri;
          channel: Iri;
          createdAt: string;
          direction: MessageDirection.Received;
          message: ConversationMessageContent;
          origin: null;
          read: boolean;
          messageType: ConversationMessageType.SmsReceived;
          agentExplanation: string | null;
      }
    | {
          id: string;
          conversation: Iri;
          channel: Iri;
          createdAt: string;
          direction: MessageDirection.Received;
          message: ConversationMessageContent;
          origin: null;
          read: boolean;
          messageType: ConversationMessageType.WhatsappBasicReceived;
          agentExplanation: string | null;
      }
    | {
          id: string;
          conversation: Iri;
          channel: Iri;
          createdAt: string;
          direction: MessageDirection.Received;
          origin: null;
          read: false;
          messageType: ConversationMessageType.UnsupportedReceived;
          agentExplanation: string | null;
      }
    | {
          id: string;
          conversation: Iri;
          channel: Iri;
          createdAt: string;
          direction: MessageDirection.Received;
          message: InteractiveConversationMessageReplyContent;
          origin: null;
          read: false;
          messageType: ConversationMessageType.WhatsappInteractiveReplyReceived;
          agentExplanation: string | null;
      }
    | {
          id: string;
          conversation: Iri;
          channel: Iri;
          createdAt: string;
          direction: MessageDirection.Received;
          message: ReceivedEmailMessageContent;
          origin: null;
          read: boolean;
          messageType: ConversationMessageType.EmailReceived;
          agentExplanation: string | null;
      }
    | {
          id: string;
          conversation: Iri;
          channel: Iri;
          createdAt: string;
          direction: MessageDirection.Received;
          sender: null | string;
          status: MessageStatus;
          message: ConversationMessageContent;
          origin: null;
          messageType: ConversationMessageType.AiReceived;
          read: boolean;
          agentExplanation: string | null;
      }
    | {
          id: string;
          conversation: Iri;
          channel: Iri;
          createdAt: string;
          direction: MessageDirection.Received;
          sender: null | string;
          status: MessageStatus;
          message: WaTemplatesConversationMessageContent;
          origin: null;
          messageType: ConversationMessageType.AIWhatsappTemplateReceived;
          read: boolean;
          agentExplanation: string | null;
          templateId: string;
      }
    | {
          id: string;
          conversation: Iri;
          channel: Iri;
          createdAt: string;
          direction: MessageDirection.Received;
          message: WebChatMessageContent;
          origin: null;
          read: boolean;
          messageType: ConversationMessageType.WebChatReceived;
          agentExplanation: string | null;
      };

export type ConversationsMessage = SentConversationMessage | ReceivedConversationMessage;

export type ConversationsMessageMediaItem = {
    type: ConversationMessageMediaItemType;
    item: Iri;
};

export type CreateConversationsMessage =
    | {
          uuid: string;
          channel: Iri;
          messageType: ConversationMessageType.WhatsappBasicSent;
          text: string;
      }
    | {
          uuid: string;
          channel: Iri;
          messageType: ConversationMessageType.WebChatSent;
          text: string;
      }
    | {
          uuid: string;
          channel: Iri;
          messageType: ConversationMessageType.WhatsappBasicSent;
          mediaItem: ConversationsMessageMediaItem;
          text: string | null;
      }
    | {
          uuid: string;
          channel: Iri;
          messageType: ConversationMessageType.WhatsappTemplateSent;
          templateId: string;
      }
    | {
          uuid: string;
          channel: Iri;
          messageType: ConversationMessageType.WhatsappTemplateSent;
          templateId: string;
          projectId: string;
          templateLocale: string;
          parameters?: Record<string, string>;
      }
    | {
          uuid: string;
          channel: Iri;
          messageType: ConversationMessageType.EmailSent;
          subject: string;
          content: {
              text: string;
              html: string;
          };
      };

export type CreateConversationSms = {
    uuid: string;
    channel: Iri;
    messageType: ConversationMessageType.SmsSent;
    text: string;
};

export type CreateConversationEmail = {
    uuid: string;
    channel: Iri;
    messageType: ConversationMessageType.EmailSent;
    subject: string;
    content: {
        text: string;
    };
};

export type TalentEntryMessage = {
    entry: TalentEntry;
    createdAt: string;
    id: string;
};

export type TalentApplicationMessage = {
    application: TalentApplication;
    createdAt: string;
    id: string;
};

export enum InboxMessageType {
    Conversation = 'conversation',
    AiConversation = 'ai_conversation',
    AgentSessionEvent = 'agent_session_event',
    TalentEntryEvent = 'talent_entry_event',
    TalentApplicationEvent = 'talent_application_event',
}

export type InboxMessage =
    | {
          type: InboxMessageType.Conversation;
          message: ConversationsMessage;
      }
    | {
          type: InboxMessageType.AiConversation;
          message: ConversationsMessage;
      }
    | {
          type: InboxMessageType.AgentSessionEvent;
          message: AgentSessionEvent;
      }
    | {
          type: InboxMessageType.TalentEntryEvent;
          message: TalentEntryMessage;
      }
    | {
          type: InboxMessageType.TalentApplicationEvent;
          message: TalentApplicationMessage;
      };

export type ConversationMessagesGroupedByAgentSessionEvent = {
    id: string;
    messages: InboxMessage[];
};

export type ConversationLatestMessagePreview = {
    text: string | null;
    createdAt: string;
    direction: MessageDirection;
    status: MessageStatus | null;
    channelType: ChannelType;
    channelId: string;
    attachmentType: AttachmentType | null;
    messageType: ConversationMessageType;
    options?: string[] | null;
    unsupported: boolean;
} | null;
